// Route constants for the web application
// Centralized definition of all route paths to ensure consistency

export const ROUTES = {
  // Public/Marketing Routes
  LANDING: "/",
  PRIVACY: "/privacy",
  TERMS: "/terms",
  SECURITY_POLICY: "/security-policy",

  // Authentication Routes
  SIGNIN: "/signin",
  SIGNUP: "/signup",
  RESET_PASSWORD: "/reset-password",
  DRAFT_INVITE_AUTH: "/draft-invite-auth",
  ENTER_INVITE_CODE: "/enter-invite-code",
  SIGNUP_INVITE_CODE: "/signup/invite-code",
  SMS_CONSENT: "/sms-consent",
  VERIFY_OTP: "/verify-otp",
  DRAFT_INVITE_SUCCESS: "/draft-invite-success",
  COMPANY_INVITE_SUCCESS: "/company-invite-success",
  STUDENT_LANDING: "/student-landing",
  STUDENT_SCHEDULE: "/student/schedule",
  STUDENT_MESSAGES: "/student/messages",
  STUDENT_JOURNAL: "/student/journal",

  // Dynamic Auth Routes (patterns)
  DRAFT_INVITE: "/draft-invite",
  DRAFT_INVITE_WITH_TOKEN: "/draft-invite/:inviteToken",
  STUDENT_INVITE: "/signup/:companyId",
  LOCATION_INVITE: "/signup/:companyId/:locationId",

  // Company Invitation
  ACCEPT_COMPANY_INVITATION: "/accept-company-invitation",

  // Onboarding Routes
  OWNER_SIGNUP: "/owner-signup",
  ONBOARDING: "/onboarding",
  ONBOARDING_LOCATIONS: "/onboarding/locations",
  ONBOARDING_BILLING: "/onboarding/billing",
  STUDENT_ONBOARDING: "/student-onboarding",
  STUDENT_ONBOARDING_COMPLETE: "/student-onboarding/complete",
  GUIDED_STUDENT_ONBOARDING: "/guided-student-onboarding",

  // Deep Link Routes (bypass Layout)
  JOIN: "/join",

  // Dashboard Routes
  DASHBOARD: "/dashboard",
  ACTIVITY: "/activity",
  PAYMENTS: "/payments",
  WEB_HOSTING_INTEREST: "/web-hosting-interest",
  STUDENTS: "/students",
  UPLOAD_STUDENT_DATA: "/upload-student-data",

  // Damage Waivers
  DAMAGE_WAIVERS: "/damage-waivers",
  DAMAGE_WAIVERS_UPLOAD: "/damage-waivers/upload",
  DAMAGE_WAIVERS_TEMPLATE_FIELDS: "/damage-waivers/:templateId/fields",
  DAMAGE_WAIVERS_COMPLIANCE: "/damage-waivers/compliance",

  // QR Codes
  QR_CODES: "/qr-codes",

  // Company Management
  COMPANY_SELECTOR: "/company-selector",
  CREATE_GYM: "/create-gym",

  // Billing Routes
  BILLING: "/billing",
  BILLING_UNIFIED: "/billing/unified",
  BILLING_SETTINGS: "/billing/settings",
  BILLING_MANUAL_PAYMENTS: "/billing/manual-payments",
  BILLING_REPORTS: "/billing/reports",
  BILLING_PAYMENTS: "/billing/payments",
  BILLING_OUTSTANDING: "/billing/outstanding",
  BILLING_DISCOUNT_CODES: "/billing/discount-codes",

  // Legacy Billing Routes (redirects)
  SETTINGS_BILLING: "/settings/billing",
  BILLING_SETTINGS_REDIRECT: "/billing-settings",

  // Inventory & POS
  INVENTORY_DASHBOARD: "/inventory",
  INVENTORY_PRODUCTS: "/inventory/products",
  INVENTORY_VARIANT_DETAIL: "/inventory/variants/:variantId",

  // Subscription Management
  SUBSCRIPTIONS_PLANS: "/subscriptions-plans",
  SUBSCRIPTIONS_PLANS_ROLLOPS: "/subscriptions-plans/rollops",
  SUBSCRIPTIONS_PLANS_GYM: "/subscriptions-plans/gym",

  // Legacy Subscription Route (redirects)
  SUBSCRIPTIONS: "/subscriptions",

  // Discount Codes (standalone; use BILLING_DISCOUNT_CODES for billing tab)
  DISCOUNT_CODES: "/discount-codes",

  // Account Settings
  ACCOUNT_SETTINGS: "/account-settings",

  // Belts & Ranks (owner profile management; #746)
  BELTS: "/belts",
  BELT_PROFILE_EDIT: "/belts/:profileId/edit",

  // Per-student rank lifecycle (#747)
  STUDENT_BELT_RANKS: "/students/:studentId/ranks",

  // Promotion workflow (#749)
  BELT_PROMOTIONS: "/belts/promotions",

  // Rank views (#750) — student-facing progress + owner-facing history/distribution
  MY_RANK: "/my-rank",
  BELT_HISTORY: "/belts/history",

  // Content Routes
  FEED: "/feed",
  SCHEDULE: "/schedule",
  SCHEDULE_SETTINGS: "/schedule/settings",
  TIME_CARD: "/time-card",
  NOTES: "/notes",

  // Social Management
  SOCIAL: "/social",
  SOCIAL_MODERATION: "/social/moderation",
  SOCIAL_ANALYTICS: "/social/analytics",

  // Admin Routes
  ADMIN_DASHBOARD: "/admin/dashboard",
  ADMIN_BACKEND_TOOLS: "/admin/backend-tools",
  ADMIN_CUSTOMERS: "/admin/customers",
  ADMIN_SURVEY_BUILDER: "/admin/survey-builder",
  ADMIN_SUPPORT_REQUESTS: "/admin/support-requests",
  ADMIN_STRIPE_MANAGEMENT: "/admin/stripe-management",
  ADMIN_ROLLOPS_SUBSCRIPTIONS: "/admin/rollops-subscriptions",
  ADMIN_SUBSCRIPTION_MANAGEMENT: "/admin/subscription-management",
  ADMIN_DEFAULT_WAIVER: "/admin/default-waiver",
  ADMIN_FEE_SCHEDULES: "/admin/fee-schedules",
  ADMIN_FEE_SCHEDULE_DETAILS: "/admin/fee-schedules/:feeScheduleId",
  ADMIN_JOBS: "/admin/jobs",
  ADMIN_MANAGEMENT: "/admin/admin-management",
  ADMIN_QR_GENERATOR: "/admin/qr-generator",

  // Developer Routes
  DEV_DASHBOARD: "/dev/dashboard",
  DEV_COMPANIES: "/dev/companies",
  DEV_IMPERSONATE: "/dev/impersonate",

  // Utility Routes
  DELETE_ACCOUNT: "/delete-account",
  GET_INFO: "/get-info",
  SMS_CONSENT_LEGAL: "/sms-consent-legal",

  // Founders Promo
  FOUNDERS_PROMO: "/founders-promo",

  // Feature Pages
  FEATURES_MEMBER_MANAGEMENT: "/features/member-management",
  FEATURES_BILLING: "/features/billing",
  FEATURES_SCHEDULING: "/features/scheduling",
} as const;

// Type helper for route values
export type RoutePath = (typeof ROUTES)[keyof typeof ROUTES];
